"use client";

import { Slider, SliderTrack, SliderThumb } from "react-aria-components";
import { useTranslations } from "next-intl";
import clsx from "clsx";
import { formatNumberToKM } from "@utilities/numbers";

import { SLIDER_MIN, SLIDER_MAX, sliderToValue, formatUsage } from "./calculatorUtils";

const LABEL_THRESHOLD = 60;

export interface UsageSliderProps {
  /** Raw slider value (0-100) */
  sliderValue: number;
  /** Called with raw slider value on change */
  onSliderChange: (sliderValue: number) => void;
  /** Maximum value for the slider scale, in `unitSuffix` units. */
  maxValue: number;
  /** Translated short suffix appended to the value bubble (e.g. "min", "characters", "h"). */
  unitSuffix: string;
  /** Aria label for the slider */
  ariaLabel?: string;
}

export function UsageSlider({ sliderValue, onSliderChange, maxValue, unitSuffix, ariaLabel }: UsageSliderProps) {
  const t = useTranslations("PricingClientKeys.PricingCard");
  const value = sliderToValue(sliderValue, maxValue);
  const formattedValue = formatUsage(value, unitSuffix);
  const formattedMax = formatNumberToKM(maxValue);

  const isLabelInside = sliderValue >= LABEL_THRESHOLD;
  const scale = sliderValue / 100;

  return (
    <div className='has-[[data-dragging]]:tw-cursor-grabbing'>
      <div className='tw-relative tw-w-full tw-h-9 tw-rounded-[10px] tw-bg-gray-100 has-[:focus-visible]:tw-outline-focus'>
        {/* Clip container for the fill - handles border radius */}
        <div className='tw-absolute tw-inset-0 tw-rounded-[10px] tw-overflow-hidden'>
          {/* Black fill - uses scaleX for GPU acceleration */}
          <div
            className='tw-absolute tw-inset-0 tw-bg-black tw-origin-left'
            style={{ transform: `scaleX(${scale})` }}
          />
        </div>

        {/* Positioning container - matches fill width, holds handle and label */}
        <div className='tw-absolute tw-inset-y-0 tw-left-0 tw-pointer-events-none' style={{ width: `${sliderValue}%` }}>
          {/* Handle - fixed position from right edge */}
          {sliderValue > 0 && (
            <div className='tw-absolute tw-right-1 tw-top-1/2 tw--translate-y-1/2 tw-w-0.5 tw-h-5 tw-bg-white/40 tw-rounded-sm' />
          )}

          {/* Label - fixed right: 0, uses transform to animate between inside/outside */}
          <div
            className={clsx(
              "tw-absolute tw-right-0 tw-inset-y-0 tw-flex tw-items-center tw-type-base tw-tabular-nums tw-whitespace-nowrap tw-transition-[color,transform] tw-duration-200 tw-ease-out",
              isLabelInside ? "tw-text-white" : "tw-text-black",
            )}
            style={{
              transform: isLabelInside ? "translateX(-12px)" : "translateX(calc(100% + 12px))",
            }}
          >
            {formattedValue}
          </div>
        </div>

        {/* Slider for interaction - React Aria handles keyboard controls (arrow keys) */}
        <Slider
          className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-z-10'
          value={sliderValue}
          onChange={onSliderChange}
          minValue={SLIDER_MIN}
          maxValue={SLIDER_MAX}
          step={1}
          aria-label={ariaLabel || t("calculator.estimated_minutes_per_month")}
        >
          <SliderTrack className='tw-relative tw-h-full tw-w-full tw-grow tw-cursor-pointer'>
            {/* Visually hidden thumb - focus ring shown on container via focus-within */}
            <SliderThumb className='tw-block tw-w-4 tw-h-full tw-opacity-0 tw-cursor-grab tw-outline-none' />
          </SliderTrack>
        </Slider>
      </div>

      {/* Range labels */}
      <div className='tw-flex tw-justify-between tw-mt-1 tw-text-xs tw-text-gray-400 tw-tabular-nums'>
        <span>0</span>
        <span>{t("calculator.slider_max_label", { max: formattedMax })}</span>
      </div>
    </div>
  );
}
